import { useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "../store";
import { selectEffectiveLanguages } from "../store/selectors/authSelectors";
import { logger } from "../utils/logger";

/**
 * Hook that strictly binds UI language to user's nativeLanguage.
 * Rules:
 * - UI language = user.nativeLanguage (if supported), otherwise 'en'.
 * - Persist to localStorage (i18next will store under i18nextLng) so login screens and subsequent sessions use it.
 * - Update automatically when nativeLanguage changes in profile.
 * - No manual switching in the client UI.
 */
export const useInterfaceLanguage = (): {
  currentLanguage: string;
  changeLanguage: (lng: string) => void; // kept for rare system use (not exposed in UI)
  supportedLanguages: string[];
  resetLanguageChoice: () => void;
} => {
  const { t, i18n } = useTranslation();
  const { nativeLanguage } = useAppSelector(selectEffectiveLanguages);

  const applyLanguage = useCallback(
    (lng: string) => {
      const supported = (i18n.options.supportedLngs || []) as string[];
      const target = supported.includes(lng) ? lng : "en";
      if (i18n.language !== target) {
        logger.debug("[useInterfaceLanguage] applying language", { target });
        i18n.changeLanguage(target);
      }
    },
    [i18n],
  );

  // On mount and whenever user/profile changes, enforce UI language
  useEffect(() => {
    if (nativeLanguage) {
      logger.debug(
        "[useInterfaceLanguage] Syncing UI language with native language",
        { nativeLanguage },
      );
      applyLanguage(nativeLanguage);
    } else {
      // No native language known yet (e.g., before auth) — keep current or fallback to en
      applyLanguage(i18n.language || "en");
    }
  }, [nativeLanguage, applyLanguage, i18n.language]);

  useEffect((): void => {
    document.documentElement.lang = i18n.resolvedLanguage || "en";
    document.title = t("common:meta.title");
    document
      .querySelector<HTMLMetaElement>('meta[name="description"]')
      ?.setAttribute("content", t("common:meta.description"));
  }, [t, i18n, i18n.resolvedLanguage]);

  // Public API (not shown in UI)
  const resetLanguageChoice = useCallback(() => {
    localStorage.removeItem("i18nextLng");
    logger.debug("[useInterfaceLanguage] language choice reset");
  }, []);

  const changeLanguage = useCallback(
    (lng: string) => {
      // Primarily for system actions/tests. In normal flow we do not expose UI switching.
      applyLanguage(lng);
    },
    [applyLanguage],
  );

  return {
    currentLanguage: i18n.language,
    changeLanguage,
    supportedLanguages: [...((i18n.options.supportedLngs || []) as string[])],
    resetLanguageChoice,
  };
};
